import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fileToDataUrl } from "@/lib/format";
import { uploadMedia } from "@/lib/upload";
import { resolveMediaUrl } from "@/lib/media";
import { useAuth } from "@/contexts/AuthContext";
import AdminNav from "@/components/AdminNav";
import "@/styles/portfolio.css";
import "@/styles/admin.css";

const EMPTY = {
  title: "",
  client: "",
  video_url: "",
  video_filename: "",
  video_public_id: "",
  poster_url: "",
  alt_text: "",
  instagram_link: "",
  tone: "warm",
  order: 0,
  published: true,
};

const TONE_OPTIONS = [
  { value: "warm", label: "Warm (Restaurant)" },
  { value: "rose", label: "Rose (Praxis)" },
  { value: "amber", label: "Amber (Café)" },
];

export default function AdminReels() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reels");
      setItems(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      title: it.title || "",
      client: it.client || "",
      video_url: it.video_url || "",
      video_filename: it.video_filename || "",
      video_public_id: it.video_public_id || "",
      poster_url: it.poster_url || "",
      alt_text: it.alt_text || "",
      instagram_link: it.instagram_link || "",
      tone: it.tone || "warm",
      order: it.order || 0,
      published: it.published !== false,
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setUploadProgress(0);
  };

  const handleVideoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setError("");
    setUploadProgress(0);
    try {
      // 1. Get a signed upload signature from our backend.
      const { data: sig } = await api.get(
        "/admin/cloudinary/signature?folder=reels&resource_type=video"
      );

      // 2. Upload the file DIRECTLY to Cloudinary (no proxy through our
      //    backend → no 60s ingress timeout, browser → Cloudinary CDN).
      //    We use XMLHttpRequest because the `fetch` API has no native
      //    upload-progress events.
      const url = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${sig.resource_type}/upload`;
      const fd = new FormData();
      fd.append("file", f);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);

      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (err) {
              reject(new Error("Ungültige Cloudinary-Antwort."));
            }
          } else {
            try {
              const errBody = JSON.parse(xhr.responseText);
              reject(
                new Error(
                  errBody?.error?.message ||
                    `Cloudinary HTTP ${xhr.status}`
                )
              );
            } catch (_e) {
              reject(new Error(`Cloudinary HTTP ${xhr.status}`));
            }
          }
        };
        xhr.onerror = () => reject(new Error("Netzwerkfehler beim Upload."));
        xhr.send(fd);
      });

      // 3. Build a delivery URL with `f_auto,q_auto` so Cloudinary picks
      //    the best codec/format for each viewer's browser. This is the
      //    single thing that eliminates the "black playback" problem.
      const baseUrl = result.secure_url || result.url || "";
      const deliveryUrl = baseUrl.replace(
        "/video/upload/",
        "/video/upload/f_auto,q_auto/"
      );

      setForm((prev) => ({
        ...prev,
        video_url: deliveryUrl,
        video_public_id: result.public_id || "",
        video_filename: prev.video_filename || result.original_filename || f.name,
      }));
    } catch (err) {
      setError(err.message || formatApiError(err) || "Video konnte nicht hochgeladen werden.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handlePosterUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setError("");
    try {
      const dataUrl = await fileToDataUrl(f, 1200, 0.85);
      // Send small images directly as base64 via the same upload endpoint?
      // For simplicity we upload via multipart (poster files are usually small).
      const result = await uploadMedia(f);
      setForm((prev) => ({ ...prev, poster_url: result.url }));
      // discard dataUrl, just used for early validation
      void dataUrl;
    } catch (err) {
      setError("Poster konnte nicht hochgeladen werden.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (editing === "new") {
        await api.post("/admin/reels", form);
      } else {
        await api.patch(`/admin/reels/${editing}`, form);
      }
      await load();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Reel „${it.title}" löschen?`)) return;
    try {
      await api.delete(`/admin/reels/${it.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell" data-testid="admin-reels">
      <header className="admin-header">
        <div>
          <span className="eyebrow">ADMIN · BEISPIEL-REELS</span>
          <h1 className="display admin-h1">
            Deine <span className="italic">Reels</span>.
          </h1>
        </div>
        <div className="admin-header__actions">
          <button type="button" onClick={startNew} className="btn btn--solid" data-testid="reel-new">
            <span>Neues Reel</span>
            <span className="btn__arrow">→</span>
          </button>
          <button type="button" onClick={handleLogout} className="btn btn--ghost">
            <span>Abmelden</span>
          </button>
        </div>
      </header>

      <AdminNav active="/admin/reels" />

      {error && <div className="admin-error">{error}</div>}

      {editing && (
        <form onSubmit={submit} className="admin-form" data-testid="reel-form">
          <div className="admin-field">
            <span className="admin-label">Video-Upload (MP4 / MOV / WebM)</span>
            {form.video_url ? (
              <div className="admin-cover-preview">
                <video
                  src={resolveMediaUrl(form.video_url)}
                  controls
                  muted
                  playsInline
                  style={{ width: "100%", maxHeight: 320, background: "#000" }}
                />
                <div className="admin-cover-actions">
                  <label className="admin-link admin-link--accent">
                    Video ersetzen
                    <input type="file" accept="video/*" onChange={handleVideoUpload} hidden />
                  </label>
                  <button
                    type="button"
                    className="admin-link admin-link--danger"
                    onClick={() => setForm({ ...form, video_url: "", video_filename: "", video_public_id: "" })}
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ) : (
              <label className="admin-upload">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  hidden
                  data-testid="reel-video-input"
                />
                <span className="admin-upload__plus">+</span>
                <span className="admin-upload__label">
                  {uploading ? `Lädt hoch… ${uploadProgress}%` : "Video hochladen"}
                </span>
                <span className="admin-upload__hint">9:16 Hochformat · jedes Format · direkt zu Cloudinary</span>
              </label>
            )}
          </div>

          <label className="admin-field">
            <span className="admin-label">
              Dateiname <span className="admin-hint">(SEO — wird vor dem Upload gesetzt)</span>
            </span>
            <input
              type="text"
              className="admin-input"
              value={form.video_filename}
              onChange={(e) => setForm({ ...form, video_filename: e.target.value })}
              placeholder="reel-restaurant-baden-sommer-2026"
              data-testid="reel-filename"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">Titel (intern)</span>
            <input
              type="text"
              className="admin-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              placeholder="Restaurant · Baden"
              data-testid="reel-title"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">Kunde (Anzeige unter dem Reel)</span>
            <input
              type="text"
              className="admin-input"
              value={form.client}
              onChange={(e) => setForm({ ...form, client: e.target.value })}
              placeholder="Restaurant Sonne · Baden"
              data-testid="reel-client"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">
              Alt-Text <span className="admin-hint">(SEO + Barrierefreiheit)</span>
            </span>
            <input
              type="text"
              className="admin-input"
              value={form.alt_text}
              onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
              placeholder="Instagram Reel für Restaurant Sonne in Baden — content.by.max"
              data-testid="reel-alt"
            />
          </label>

          <label className="admin-field">
            <span className="admin-label">Instagram-Link (Reel-URL)</span>
            <input
              type="url"
              className="admin-input"
              value={form.instagram_link}
              onChange={(e) => setForm({ ...form, instagram_link: e.target.value })}
              placeholder="https://www.instagram.com/reel/XXXXXXX/"
              data-testid="reel-ig-link"
            />
          </label>

          <div className="admin-field">
            <span className="admin-label">Poster-Bild (optional, wird vor dem Video gezeigt)</span>
            {form.poster_url ? (
              <div className="admin-cover-preview">
                <img src={resolveMediaUrl(form.poster_url)} alt="Poster" />
                <div className="admin-cover-actions">
                  <label className="admin-link admin-link--accent">
                    Ersetzen
                    <input type="file" accept="image/*" onChange={handlePosterUpload} hidden />
                  </label>
                  <button
                    type="button"
                    className="admin-link admin-link--danger"
                    onClick={() => setForm({ ...form, poster_url: "" })}
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ) : (
              <label className="admin-upload">
                <input type="file" accept="image/*" onChange={handlePosterUpload} hidden />
                <span className="admin-upload__plus">+</span>
                <span className="admin-upload__label">Poster-Bild hochladen</span>
              </label>
            )}
          </div>

          <div className="admin-row-two">
            <label className="admin-field">
              <span className="admin-label">Farbton (Fallback ohne Video)</span>
              <select
                className="admin-input"
                value={form.tone}
                onChange={(e) => setForm({ ...form, tone: e.target.value })}
                data-testid="reel-tone"
              >
                {TONE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="admin-field">
              <span className="admin-label">Reihenfolge</span>
              <input
                type="number"
                className="admin-input"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                data-testid="reel-order"
              />
            </label>
          </div>

          <label className="admin-toggle">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
            />
            <span>Veröffentlicht (in &bdquo;Arbeit&ldquo;-Sektion sichtbar)</span>
          </label>

          <div className="admin-form-foot">
            <button type="button" className="btn btn--ghost" onClick={cancel}>
              <span>Abbrechen</span>
            </button>
            <button type="submit" disabled={busy || uploading} className="btn btn--solid" data-testid="reel-save">
              <span>{busy ? "Speichert…" : "Speichern"}</span>
              <span className="btn__arrow">→</span>
            </button>
          </div>
        </form>
      )}

      {!editing && (
        <section className="admin-posts">
          {loading && <p className="admin-empty">Lädt…</p>}
          {!loading && items.length === 0 && (
            <p className="admin-empty">Noch keine Reels — lade dein erstes Beispiel hoch.</p>
          )}
          <ul className="admin-post-list" data-testid="reel-list">
            {items.map((it) => (
              <li key={it.id} className="admin-post-row" data-testid={`reel-${it.id}`}>
                <div className="admin-post-row__main">
                  <span className="admin-post-row__date">
                    {it.tone?.toUpperCase()} · Order {it.order}
                    {!it.published && <em className="admin-draft"> · Entwurf</em>}
                  </span>
                  <h2 className="admin-post-row__title">{it.title}</h2>
                  <p className="admin-post-row__excerpt">
                    {it.client || "—"} · {it.video_filename || "(kein Video)"}
                  </p>
                  {it.alt_text && <span className="admin-hint">Alt: {it.alt_text}</span>}
                  {it.instagram_link && (
                    <a
                      href={it.instagram_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-link admin-link--accent"
                      style={{ marginTop: 4 }}
                    >
                      Instagram öffnen ↗
                    </a>
                  )}
                </div>
                <div className="admin-post-row__actions">
                  <button className="admin-link admin-link--accent" onClick={() => startEdit(it)}>
                    Bearbeiten
                  </button>
                  <button className="admin-link admin-link--danger" onClick={() => remove(it)}>
                    Löschen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link to="/admin" className="admin-back" style={{ marginTop: 32 }}>
        ← Zurück zur Übersicht
      </Link>
    </div>
  );
}
